import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { DateInputTR } from "@/components/DateInputTR";
import { todayISO } from "@/lib/dateTR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ChartBar, DownloadSimple, FilePdf, CurrencyCircleDollar, Receipt, Buildings, Users,
} from "@phosphor-icons/react";

function daysAgoISO(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function ExpenseReports() {
  const { user } = useAuth();
  const isAdmin = !!user;

  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    start: daysAgoISO(90),
    end: todayISO(),
    site_id: "__all__",
    user_id: "__all__",
    status: "__all__",
  });

  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API}/sites`);
        setSites(s.data || []);
        if (isAdmin) {
          const u = await axios.get(`${API}/users`);
          setUsers(u.data || []);
        }
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { start: filters.start, end: filters.end };
      if (filters.site_id !== "__all__") params.site_id = filters.site_id;
      if (filters.user_id !== "__all__") params.user_id = filters.user_id;
      if (filters.status !== "__all__") params.status = filters.status;
      const { data } = await axios.get(`${API}/expenses/reports/summary`, { params });
      setSummary(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const exportUrl = (fmt) => {
    const params = new URLSearchParams();
    params.set("start", filters.start); params.set("end", filters.end);
    if (filters.site_id !== "__all__") params.set("site_id", filters.site_id);
    if (filters.user_id !== "__all__") params.set("user_id", filters.user_id);
    if (filters.status !== "__all__") params.set("status", filters.status);
    return `${API}/expenses/export.${fmt}?${params.toString()}`;
  };

  const maxBy = (arr, key) => Math.max(1, ...(arr || []).map(x => x[key] || 0));

  return (
    <Shell
      title="Masraf Raporları"
      subtitle="Masraf Bildirimi › Raporlar"
      hideSiteSelector
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(exportUrl("xlsx"), "_blank")}
                  data-testid="report-export-xlsx"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <DownloadSimple size={14} className="mr-1.5" /> Excel
          </Button>
          <Button variant="outline" onClick={() => window.open(exportUrl("pdf"), "_blank")}
                  data-testid="report-export-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            <FilePdf size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      }
    >
      {/* Filtre şeridi */}
      <Card className="p-3 rounded-none sm:rounded-sm mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Başlangıç</Label>
          <DateInputTR value={filters.start} onChange={v => setFilters({ ...filters, start: v })}
                       data-testid="report-start" className="rounded-none sm:rounded-sm mt-1 h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Bitiş</Label>
          <DateInputTR value={filters.end} onChange={v => setFilters({ ...filters, end: v })}
                       data-testid="report-end" className="rounded-none sm:rounded-sm mt-1 h-9" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şantiye</Label>
          <Select value={filters.site_id} onValueChange={v => setFilters({ ...filters, site_id: v })}>
            <SelectTrigger className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tümü</SelectItem>
              {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isAdmin && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Personel</Label>
            <Select value={filters.user_id} onValueChange={v => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tümü</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Durum</Label>
          <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="rounded-none sm:rounded-sm mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tümü</SelectItem>
              <SelectItem value="pending">Bekliyor</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading || !summary ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : (
        <>
          {/* Üst özet */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="report-summary-cards">
            <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <Receipt size={11} /> Kayıt Sayısı
              </div>
              <div className="text-3xl font-bold text-[#1B4776] mt-1 font-heading">{summary.count}</div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-1">
                <CurrencyCircleDollar size={11} /> Toplam Brüt
              </div>
              <div className="text-2xl font-bold text-[#1B4776] mt-1 font-mono">
                ₺{summary.total_gross.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
              </div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Net</div>
              <div className="text-2xl font-bold text-slate-800 mt-1 font-mono">
                ₺{summary.total_net.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
              </div>
            </Card>
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Toplam KDV</div>
              <div className="text-2xl font-bold text-amber-600 mt-1 font-mono">
                ₺{summary.total_vat.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
              </div>
            </Card>
          </div>

          {/* Duruma göre */}
          <Card className="p-4 rounded-none sm:rounded-sm mb-4">
            <div className="flex items-center gap-2 mb-3">
              <ChartBar size={18} weight="duotone" className="text-[#95C11F]" />
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Durum Dağılımı</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "pending",  label: "Bekliyor",  color: "#F59E0B" },
                { key: "approved", label: "Onaylandı", color: "#95C11F" },
                { key: "rejected", label: "Reddedildi", color: "#EF4444" },
              ].map(s => (
                <div key={s.key} className="border border-slate-200 p-3">
                  <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: s.color }}>{s.label}</div>
                  <div className="text-xl font-mono font-bold text-slate-800 mt-1">
                    ₺{(summary.by_status?.[s.key] || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Kategoriye göre bar */}
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Receipt size={16} weight="duotone" className="text-[#95C11F]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Kategoriye Göre</div>
              </div>
              {summary.by_category.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-4 text-center">Veri yok</div>
              ) : (
                <div className="space-y-1.5">
                  {summary.by_category.slice(0, 8).map(c => {
                    const pct = (c.total / maxBy(summary.by_category, "total")) * 100;
                    return (
                      <div key={c.key}>
                        <div className="flex justify-between text-xs">
                          <span className="font-medium">{c.label}</span>
                          <span className="font-mono font-bold text-[#1B4776]">
                            ₺{c.total.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                            <span className="text-slate-400 ml-1">({c.count})</span>
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 mt-1">
                          <div className="h-full bg-[#95C11F]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Şantiyeye göre */}
            <Card className="p-4 rounded-none sm:rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Buildings size={16} weight="duotone" className="text-[#1B4776]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Şantiyeye Göre</div>
              </div>
              {summary.by_site.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-4 text-center">Veri yok</div>
              ) : (
                <div className="space-y-1.5">
                  {summary.by_site.slice(0, 8).map(s => {
                    const pct = (s.total / maxBy(summary.by_site, "total")) * 100;
                    return (
                      <div key={s.site_id}>
                        <div className="flex justify-between text-xs">
                          <span className="font-medium">{s.site_name}</span>
                          <span className="font-mono font-bold text-[#1B4776]">
                            ₺{s.total.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 mt-1">
                          <div className="h-full bg-[#1B4776]" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {isAdmin && (
            <Card className="p-4 rounded-none sm:rounded-sm mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} weight="duotone" className="text-[#95C11F]" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700">Personele Göre</div>
              </div>
              {summary.by_user.length === 0 ? (
                <div className="text-xs text-slate-400 italic py-4 text-center">Veri yok</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-200">
                    <tr><th className="text-left py-1">Personel</th>
                        <th className="text-right py-1">Kayıt</th>
                        <th className="text-right py-1">Toplam Brüt</th></tr>
                  </thead>
                  <tbody>
                    {summary.by_user.slice(0, 12).map(u => (
                      <tr key={u.user_id} className="border-b border-slate-100">
                        <td className="py-1.5">{u.user_name}</td>
                        <td className="py-1.5 text-right font-mono">{u.count}</td>
                        <td className="py-1.5 text-right font-mono font-bold text-[#1B4776]">
                          ₺{u.total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}
        </>
      )}
    </Shell>
  );
}
